import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import { LocationIdSchema, WebsitePerformanceReportIdSchema } from '@brightlocal/shared';
import type { LocationId } from '@brightlocal/shared';

import { PaginationSchema } from '../../../schemas';

/** Query params of the paginated Website Performance reports list. */
export interface WebsitePerformanceGetReportsParams {
  /** Filter the list to a single location's report. */
  locationId?: LocationId;
  /** 1-indexed page number; the API defaults to 1 when omitted. */
  page?: number;
  /** Items per page (1-100); the API defaults to 25 when omitted. */
  numPerPage?: number;
}

/**
 * Single entry of the reports list. `website_url` is the website analysed in the report's most
 * recent run, as recorded at the time — not guaranteed to be an absolute URI.
 */
export const WebsitePerformanceReportListItemSchema = z
  .object({
    report_id: WebsitePerformanceReportIdSchema,
    location_id: LocationIdSchema,
    website_url: z.string()
  })
  .transform((data) => camelcaseKeys(data));

export type WebsitePerformanceReportListItem = z.infer<typeof WebsitePerformanceReportListItemSchema>;

/** Paginated list of the account's Website Performance reports. Only reports with at least one run are listed. */
export const WebsitePerformanceReportsResponseSchema = z.object({
  data: z.array(WebsitePerformanceReportListItemSchema),
  pagination: PaginationSchema
});

export type WebsitePerformanceReportsResponse = z.infer<typeof WebsitePerformanceReportsResponseSchema>;

/** Overall health score of a run. Issue counts are `null` when not reported. */
const WebsitePerformanceHealthScoreSchema = z.object({
  score: z.number(),
  band: z.string(),
  critical_issues_count: z.number().nullable(),
  total_issues_count: z.number().nullable()
});

/** Written summary of a run; `health_score` is `null` when the analysis has no score. */
const WebsitePerformanceHealthSummarySchema = z.object({
  overview: z.string(),
  health_score: WebsitePerformanceHealthScoreSchema.nullable(),
  top_priorities: z.array(z.string()),
  quick_wins: z.array(z.string()),
  key_opportunities: z.array(z.string()),
  narrative: z.string().nullable()
});

/**
 * A problem found on the website. `issue_id` is unique within the run and referenced by
 * recommendations; text fields are an empty string when none was written.
 */
const WebsitePerformanceIssueSchema = z.object({
  issue_id: z.string(),
  title: z.string(),
  category: z.string(),
  severity: z.string(),
  description: z.string(),
  impact: z.string(),
  affected_page_count: z.number().nullable()
});

/** A recommended action, addressing the issues listed in `issue_ids`. */
const WebsitePerformanceRecommendationSchema = z.object({
  title: z.string(),
  description: z.string(),
  priority: z.string(),
  impact: z.string(),
  effort: z.string(),
  issue_ids: z.array(z.string()),
  steps: z.array(z.string()),
  expected_outcome: z.string()
});

/** The Website Health result of a run. Issues and recommendations keep the order the analysis reported. */
const WebsitePerformanceResultSchema = z.object({
  summary: WebsitePerformanceHealthSummarySchema,
  issues: z.array(WebsitePerformanceIssueSchema),
  recommendations: z.array(WebsitePerformanceRecommendationSchema)
});

/**
 * Latest completed run of a report. `website_url` is the website that was analysed, `created_at`
 * is when it was analysed (RFC 3339, UTC), and `result` is `null` when no analysis was recorded.
 */
export const WebsitePerformanceRunSchema = z
  .object({
    run_id: z.string(),
    report_id: WebsitePerformanceReportIdSchema,
    location_id: LocationIdSchema,
    website_url: z.string(),
    status: z.string(),
    created_at: z.string(),
    completed_at: z.string().nullable(),
    result: WebsitePerformanceResultSchema.nullable()
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type WebsitePerformanceRun = z.infer<typeof WebsitePerformanceRunSchema>;

/** The Website Health result of a run. */
export type WebsitePerformanceResult = NonNullable<WebsitePerformanceRun['result']>;

/** A problem found on the website. */
export type WebsitePerformanceIssue = WebsitePerformanceResult['issues'][number];

/** A recommended action. */
export type WebsitePerformanceRecommendation = WebsitePerformanceResult['recommendations'][number];
